import { Placement } from "@floating-ui/react";
import { InformationCircleIcon } from "@heroicons/react/24/outline";
import { ReactElement } from "react";
import {
  Control,
  Controller,
  FieldErrors,
  FieldValues,
  Path,
  PathValue,
  RegisterOptions,
} from "react-hook-form";

import { ErrorMessage } from "../catalyst/ErrorMessage";
import { Field } from "../catalyst/Field";
import { Label } from "../catalyst/Label";
import { SliderInput } from "../SliderInput";
import { Tooltip } from "../Tooltip";

export interface SliderInputGroupProps<FormType extends FieldValues> {
  control: Control<FormType>;
  defaultValue?: number;
  disabled?: boolean;
  errors: FieldErrors<FormType>;
  fieldName: Path<FormType>;
  label: ReactElement | string;
  maxLabel?: string;
  minLabel?: string;
  rules?: RegisterOptions<FormType, Path<FormType>>;
  showValue?: boolean;
  tooltip?: {
    position: Placement;
    text: string;
  };
}

const SliderInputGroup = <FormType extends FieldValues>({
  control,
  defaultValue = 1,
  disabled = false,
  errors,
  fieldName,
  label,
  maxLabel = "5 (Expensive)",
  minLabel = "1 (Cheap)",
  rules,
  showValue = true,
  tooltip,
}: SliderInputGroupProps<FormType>): ReactElement => {
  const errorMessage = errors[fieldName]?.message as string | undefined;

  return (
    <div className="flex flex-col gap-2">
      <Field>
        <Controller
          control={control}
          defaultValue={defaultValue as PathValue<FormType, Path<FormType>>}
          name={fieldName}
          render={({ field: { onChange, value } }) => (
            <>
              {typeof label === "string" ? (
                <Label className="flex items-center">
                  {showValue ? `${label}: ${value || defaultValue}` : label}
                  {tooltip && (
                    <span className="ml-1">
                      <Tooltip
                        content={tooltip.text}
                        position={tooltip.position}
                      >
                        <InformationCircleIcon className="h-4 w-4 text-gray-500 hover:text-gray-700" />
                      </Tooltip>
                    </span>
                  )}
                </Label>
              ) : (
                label
              )}
              <div className="space-y-2">
                <SliderInput
                  disabled={disabled}
                  onChange={onChange}
                  value={value}
                />
                <div className="flex justify-between text-xs text-gray-500">
                  <span>{minLabel}</span>
                  <span>{maxLabel}</span>
                </div>
              </div>
            </>
          )}
          rules={rules}
        />
        {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
      </Field>
    </div>
  );
};

export default SliderInputGroup;
